// /admin/:slug/pilots — airfield admin's pilot roster.
//
// One screen covers the three jobs the admin needs:
//   1. Add a pilot (paste @username or tg_user_id → POST /members).
//   2. See every pilot at the airfield with verification + docs state.
//   3. Drill into anyone for verify/reject — same AdminPilot detail page
//      reached from the day view.
//
// Filter chips at the top let the admin focus on "pending" (the queue
// that actually needs their attention) without losing the full roster.

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Section, Cell, List, Spinner, Banner, Input, Button, Caption, Title,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const FILTERS = [
  { key: "all",        label: "Все" },
  { key: "pending",    label: "На проверку" },
  { key: "unverified", label: "Без статуса" },
  { key: "verified",   label: "Подтверждены" },
  { key: "rejected",   label: "Отклонены" },
];

export default function AdminPilots() {
  const { slug } = useParams();
  const navigate = useNavigate();
  usePageBack(`/admin/${slug}`);
  const [search, setSearch] = useSearchParams();
  const filter = search.get("filter") || "all";

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [query, setQuery] = useState("");
  const [addInput, setAddInput] = useState("");
  const [addError, setAddError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  useEffect(() => {
    setData(null);
    setError(null);
    api(`/api/v1/admin/airfields/${slug}/pilots?status=${filter}`)
      .then(setData)
      .catch((e) => setError(`Не удалось загрузить список пилотов: ${e.message}`));
  }, [slug, filter, reloadTick]);

  function reload() { setReloadTick((n) => n + 1); }

  // Free-text filter runs client-side — the dataset is small per airfield and
  // it keeps "search-as-you-type" snappy without a debounced round-trip.
  const filtered = useMemo(() => {
    if (!data?.pilots) return [];
    const q = query.trim().toLowerCase().replace(/^@/, "");
    if (!q) return data.pilots;
    return data.pilots.filter((p) => {
      const haystack = [
        p.first_name, p.last_name, p.middle_name,
        p.tg_username, String(p.tg_user_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [data, query]);

  async function addPilot() {
    const q = addInput.trim();
    if (!q) { setAddError("Введите @username или Telegram ID."); return; }
    setBusy(true); setAddError(null); setFeedback(null);
    try {
      const r = await api(`/api/v1/admin/airfields/${slug}/members`, {
        method: "POST", body: JSON.stringify({ query: q }),
      });
      setAddInput("");
      setFeedback({ kind: "ok", text: `Пилот добавлен. Откройте карточку, чтобы подтвердить профиль.` });
      reload();
      // Jump to the new pilot's card so the admin can immediately verify.
      if (r?.targetUserId) {
        setTimeout(() => navigate(`/admin/${slug}/pilot/${r.targetUserId}?from_pilots=1`), 250);
      }
    } catch (e) {
      // Backend returns 400 with { error, message } for validation; we get
      // "api_400" from the api() helper, so re-fetch the message via fetch.
      // Cheap path: surface a generic message that points the admin at the
      // most common cause (pilot hasn't /start'd the bot yet).
      setAddError(parseError(e.message));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(userId, name) {
    if (!window.confirm(`Снять пилота «${name}» с аэродрома? Брони сохранятся, но новые он создавать не сможет.`)) return;
    setBusy(true); setFeedback(null);
    try {
      await api(`/api/v1/admin/airfields/${slug}/members/${userId}`, { method: "DELETE" });
      setFeedback({ kind: "ok", text: "Пилот снят с аэродрома." });
      reload();
    } catch (e) {
      setFeedback({ kind: "err", text: parseError(e.message) });
    } finally {
      setBusy(false);
    }
  }

  async function copyInvite() {
    const text = data?.invite?.message;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2500); }
      catch { setFeedback({ kind: "err", text: "Не удалось скопировать. Скопируйте текст вручную." }); }
      finally { document.body.removeChild(ta); }
    }
  }

  if (error && !data) {
    return <List><Banner type="section" header="Ошибка">{error}</Banner></List>;
  }

  return (
    <List>
      <Section>
        <Cell multiline subtitle={`Аэродром · ${slug}`}>
          <Title level="3">👥 Пилоты</Title>
        </Cell>
      </Section>

      {feedback && (
        <Banner type="section"
          header={feedback.kind === "ok" ? "Готово" : "Ошибка"}>
          {feedback.text}
        </Banner>
      )}

      <Section header="Добавить пилота">
        <div style={{ padding: "8px 16px 4px", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="@username или Telegram ID"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addPilot(); }}
            />
          </div>
          <Button onClick={addPilot} disabled={busy || !addInput.trim()}>
            Добавить
          </Button>
        </div>
        {addError && (
          <div style={{ padding: "0 16px 8px", color: "var(--tgui--destructive_text_color, #d33)" }}>
            <Caption level="1">{addError}</Caption>
          </div>
        )}
        <Cell multiline>
          <Caption level="1">
            Если пилот уже запускал @rcflyers_bot — введите @username. Если ещё нет —
            введите его Telegram ID (можно узнать через /whoami или у самого пилота).
            После добавления откроется карточка для верификации.
          </Caption>
        </Cell>
        <Cell
          onClick={() => setInviteOpen((v) => !v)}
          after={inviteOpen ? "▾" : "▸"}
          subtitle="Готовый текст и ссылка с приглашением — скопируйте и отправьте"
          multiline
        >
          📨 Ссылка-приглашение
        </Cell>
        {inviteOpen && data?.invite && (
          <>
            <div className="rcf-invite-box">{data.invite.message}</div>
            <div style={{ padding: "8px 16px" }}>
              <Button onClick={copyInvite} stretched mode="filled">
                {inviteCopied ? "✓ Скопировано" : "Скопировать приглашение"}
              </Button>
            </div>
          </>
        )}
      </Section>

      <Section>
        <FilterBar
          filter={filter}
          counts={data?.counts || {}}
          onChange={(key) => setSearch({ filter: key })}
        />
        <div style={{ padding: "4px 16px 8px" }}>
          <Input
            placeholder="Поиск по имени или @username…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </Section>

      {data === null && !error && (
        <Section><Cell before={<Spinner size="m" />}>Загружаем…</Cell></Section>
      )}

      {data !== null && (
        <Section header={resultHeader(filtered.length, data.pilots.length, filter, query)}>
          {filtered.length === 0 && (
            <Cell multiline subtitle="Попробуйте другой фильтр или поиск.">
              {data.pilots.length === 0
                ? "В аэродроме пока нет пилотов — добавьте первого выше."
                : "Никого не нашли"}
            </Cell>
          )}
          {filtered.map((p) => (
            <RosterRow
              key={p.user_id}
              pilot={p}
              busy={busy}
              onOpen={() => navigate(`/admin/${slug}/pilot/${p.user_id}?from_pilots=1`)}
              onRevoke={() => revoke(p.user_id, displayName(p))}
            />
          ))}
        </Section>
      )}

      <Section>
        <Cell onClick={() => navigate(`/admin/${slug}`)}>← К панели администратора</Cell>
      </Section>
    </List>
  );
}

function FilterBar({ filter, counts, onChange }) {
  return (
    <div className="rcf-filterbar">
      {FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          className={`rcf-filter${filter === f.key ? " rcf-filter--active" : ""}`}
          onClick={() => onChange(f.key)}
        >
          {f.label}
          <span className="rcf-filter__count">{counts[f.key] ?? 0}</span>
        </button>
      ))}
    </div>
  );
}

function RosterRow({ pilot, busy, onOpen, onRevoke }) {
  const name = displayName(pilot);
  const handle = pilot.tg_username ? `@${pilot.tg_username}` : null;
  const sub = [
    handle,
    pilot.skill_class && `Уровень ${pilot.skill_class}`,
    pilot.last_booking_at && `Бронь: ${formatDateRu(pilot.last_booking_at)}`,
  ].filter(Boolean).join(" · ") || `tg ${pilot.tg_user_id}`;

  const status = pilot.verification_status || "unverified";
  return (
    <div
      className="rcf-roster"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
    >
      <div className="rcf-roster__body">
        <div className="rcf-roster__title">
          {name}
          {pilot.is_admin && <span className="rcf-pill rcf-pill--info">админ</span>}
        </div>
        <div className="rcf-roster__sub">{sub}</div>
        <div className="rcf-roster__meta">
          <VerificationPill status={status} />
          <span className={`rcf-chip rcf-chip--${pilot.fasr_ok ? "ok" : "miss"}`}>
            {pilot.fasr_ok ? "✓" : "·"} ID
          </span>
          <span className={`rcf-chip rcf-chip--${pilot.ins_ok ? "ok" : "miss"}`}>
            {pilot.ins_ok ? "✓" : "·"} Страх.
          </span>
          {pilot.docs_pending > 0 && (
            <span className="rcf-pill rcf-pill--warn">
              📄 {pilot.docs_pending} на проверке
            </span>
          )}
          {pilot.warning_count > 0 && (
            <span className="rcf-pill rcf-pill--danger">
              ⚠ {pilot.warning_count}
            </span>
          )}
        </div>
      </div>
      <div className="rcf-roster__actions">
        <span style={{ color: "var(--tgui--hint_color, #8a8a8a)", fontSize: 18 }}>›</span>
        {!pilot.is_admin && (
          <Button
            mode="plain"
            size="s"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onRevoke(); }}
          >
            ✕
          </Button>
        )}
      </div>
    </div>
  );
}

function VerificationPill({ status }) {
  if (status === "verified") return <span className="rcf-pill rcf-pill--ok">✓ Подтверждён</span>;
  if (status === "pending")  return <span className="rcf-pill rcf-pill--warn">⏳ На проверке</span>;
  if (status === "rejected") return <span className="rcf-pill rcf-pill--danger">✕ Отклонён</span>;
  return <span className="rcf-pill rcf-pill--neutral">— без статуса</span>;
}

function displayName(p) {
  return [p.last_name, p.first_name, p.middle_name].filter(Boolean).join(" ")
    || (p.tg_username ? `@${p.tg_username}` : `tg:${p.tg_user_id}`);
}

function resultHeader(shown, total, filter, query) {
  const filterLabel = FILTERS.find((f) => f.key === filter)?.label || "Все";
  if (query && shown !== total) return `${filterLabel} · ${shown} из ${total} по «${query}»`;
  return `${filterLabel} (${shown})`;
}

function formatDateRu(iso) {
  try {
    return new Date(iso).toLocaleDateString("ru-RU",
      { day: "numeric", month: "short" });
  } catch { return iso; }
}

function parseError(msg) {
  if (!msg) return "Не удалось выполнить операцию.";
  if (msg === "forbidden")     return "Нет прав администратора на этом аэродроме.";
  if (msg === "rate_limited")  return "Слишком много запросов. Подождите и повторите.";
  if (/^api_/.test(msg))       return "Сервер не ответил. Попробуйте ещё раз.";
  return msg;
}
